import { useState, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import SEO from "@/components/common/SEO";
import PageHeader from "@/components/shared/PageHeader";
import { ContactCTA } from "@/components/shared/blocks";
import { portfolio, portfolioCategories } from "@/data/site";
import { cn } from "@/lib/utils";

function SpotlightCard({ p }) {
  const ref = useRef(null);
  const [pos, setPos] = useState({ x: 50, y: 50 });
  const onMove = (e) => {
    const r = ref.current?.getBoundingClientRect();
    if (!r) return;
    setPos({ x: ((e.clientX - r.left) / r.width) * 100, y: ((e.clientY - r.top) / r.height) * 100 });
  };
  return (
    <motion.article
      layout
      initial={{ opacity: 0, y: 24 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, scale: 0.96 }}
      transition={{ duration: 0.45 }}
      ref={ref}
      onMouseMove={onMove}
      className="group relative overflow-hidden rounded-3xl border border-border bg-card"
      data-testid={`portfolio-${p.id}`}
    >
      <div className="relative h-72 overflow-hidden">
        <img src={p.image} alt={p.title} loading="lazy" className="h-full w-full object-cover grayscale transition-all duration-700 group-hover:scale-105 group-hover:grayscale-0" />
        <div
          className="pointer-events-none absolute inset-0 opacity-0 transition-opacity duration-300 group-hover:opacity-100"
          style={{ background: `radial-gradient(240px circle at ${pos.x}% ${pos.y}%, hsl(var(--primary) / 0.35), transparent 70%)` }}
        />
        <div className="absolute inset-0 bg-gradient-to-t from-secondary/70 via-transparent to-transparent" />
        <span className="absolute left-4 top-4 rounded-full bg-card/90 px-3 py-1 text-xs font-medium backdrop-blur">{p.category}</span>
      </div>
      <div className="flex items-start justify-between gap-3 p-6">
        <div>
          <h3 className="font-display text-xl font-medium">{p.title}</h3>
          <div className="mt-3 flex flex-wrap gap-1.5">
            {p.tags.map((t) => (
              <span key={t} className="rounded-full bg-muted px-2.5 py-1 text-xs text-muted-foreground">{t}</span>
            ))}
          </div>
        </div>
        <ArrowUpRight className="h-5 w-5 shrink-0 text-muted-foreground transition-all group-hover:-translate-y-0.5 group-hover:translate-x-0.5 group-hover:text-primary" />
      </div>
    </motion.article>
  );
}

export default function Portfolio() {
  const [filter, setFilter] = useState("All");
  const list = filter === "All" ? portfolio : portfolio.filter((p) => p.category === filter);

  return (
    <>
      <SEO title="Portfolio" description="Selected work from NeuroTriq TechLabs — enterprise platforms, healthcare portals, e-commerce, AI products and more." path="/portfolio" />
      <PageHeader
        overline="Portfolio"
        title="Work we're proud of"
        subtitle="A selection of platforms, apps and products we've designed and engineered for clients worldwide."
      />

      <section className="py-20 sm:py-28">
        <div className="mx-auto max-w-7xl px-5 sm:px-8">
          <div className="flex flex-wrap gap-2" data-testid="portfolio-filters">
            {portfolioCategories.map((c) => (
              <button
                key={c}
                onClick={() => setFilter(c)}
                data-testid={`portfolio-filter-${c.toLowerCase()}`}
                className={cn(
                  "rounded-full border px-5 py-2 text-sm font-medium transition-colors",
                  filter === c ? "border-primary bg-primary text-primary-foreground" : "border-border bg-card text-muted-foreground hover:border-primary/40 hover:text-foreground"
                )}
              >
                {c}
              </button>
            ))}
          </div>

          <motion.div layout className="mt-12 grid gap-6 md:grid-cols-2 lg:grid-cols-3">
            <AnimatePresence mode="popLayout">
              {list.map((p) => <SpotlightCard key={p.id} p={p} />)}
            </AnimatePresence>
          </motion.div>
        </div>
      </section>

      <ContactCTA />
    </>
  );
}
